import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs'; 
import { saveAs } from 'file-saver'; 
import ManagerSidebar from '../../components/Sidebar/ManagerSidebar';
import {
    FaMoneyBillWave, FaFilePdf, FaCalendarAlt,
    FaChartLine, FaArrowUp, FaArrowDown, FaBalanceScale, FaDownload, FaCalculator
} from 'react-icons/fa';
import './ProfitLossReport.css';

const ProfitLossReport = () => {
    const [incomeData, setIncomeData] = useState([]);
    const [expenseData, setExpenseData] = useState([]);
    const [reportSummary, setReportSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const isMounted = useRef(true);

    // ✅ Helper function to calculate months between arrival and departure
    const getCalculatedMonths = (arrival, departure) => {
        const start = new Date(arrival);
        const end = new Date(departure);
        let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        return months <= 0 ? 1 : months;
    };

    // ✅ FORMAT FUNCTION (COMMAS)
    const formatRWF = (value) => {
        return (parseFloat(value) || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) + ' RWF';
    };

    const fetchFinancialData = useCallback(async () => {
        setLoading(true);
        try {
            const [incRes, expRes] = await Promise.all([
                axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/tenants/getAll'),
                axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/expenses/getAll'),
            ]);

            if (incRes.data.success && expRes.data.success && isMounted.current) {
                setIncomeData(incRes.data.data);
                setExpenseData(expRes.data.data);
            }
        } catch (err) {
            console.error('Data fetch error:', err);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFinancialData();
        return () => { isMounted.current = false; };
    }, [fetchFinancialData]);

    const calculateTotals = useCallback(() => {
        const startFilter = startDate ? new Date(startDate) : null;
        if (startFilter) startFilter.setHours(0, 0, 0, 0);

        const endFilter = endDate ? new Date(endDate) : null;
        if (endFilter) endFilter.setHours(23, 59, 59, 999);

        const processedAndFilteredIncome = [];

        // Distribute multi-month stay allocations identical to Income Report logic
        incomeData.forEach(t => {
            const arrDate = new Date(t.arrival_date);
            const depDate = new Date(t.departure_date);
            const totalRent = parseFloat(t.rent_rwf) || 0;
            
            const totalMonths = getCalculatedMonths(t.arrival_date, t.departure_date);
            const monthlyRent = totalRent / totalMonths;

            if (totalMonths >= 2) {
                let currentIteration = new Date(arrDate.getFullYear(), arrDate.getMonth(), 1);
                
                while (currentIteration <= depDate) {
                    const isDepartureMonth = currentIteration.getMonth() === depDate.getMonth() && 
                                           currentIteration.getFullYear() === depDate.getFullYear();

                    if (isDepartureMonth && (arrDate.getMonth() !== depDate.getMonth() || arrDate.getFullYear() !== depDate.getFullYear())) {
                        currentIteration.setMonth(currentIteration.getMonth() + 1);
                        continue; 
                    }

                    const nextMonthBoundary = new Date(currentIteration.getFullYear(), currentIteration.getMonth() + 1, 0, 23, 59, 59, 999);
                    
                    const isMonthInFilter = 
                        (!startFilter || nextMonthBoundary >= startFilter) && 
                        (!endFilter || currentIteration <= endFilter);

                    if (isMonthInFilter) {
                        const trackingDate = new Date(currentIteration.getFullYear(), currentIteration.getMonth(), Math.min(arrDate.getDate(), 28));
                        
                        processedAndFilteredIncome.push({
                            ...t,
                            arrival_date: trackingDate.toISOString(), 
                            rent_rwf: monthlyRent 
                        });
                    }
                    currentIteration.setMonth(currentIteration.getMonth() + 1);
                }
            } else {
                if ((!startFilter || arrDate >= startFilter) && (!endFilter || arrDate <= endFilter)) {
                    processedAndFilteredIncome.push({
                        ...t,
                        rent_rwf: totalRent 
                    });
                }
            }
        });

        // Filter standard individual expenses against date range bounds
        const filteredExp = expenseData.filter(e => {
            const d = new Date(e.Date || e.date);
            return (!startFilter || d >= startFilter) && (!endFilter || d <= endFilter);
        });

        const totalInc = processedAndFilteredIncome.reduce((sum, t) => sum + (parseFloat(t.rent_rwf) || 0), 0);
        const totalExp = filteredExp.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
        const netProfitLoss = totalInc - totalExp;

        // ✅ Calculate active month span starting from January of the current year (or earliest active transaction month)
        const currentYear = new Date().getFullYear();
        const janFirst = new Date(currentYear, 0, 1);
        
        let activeStartDate = startFilter ? new Date(startFilter) : janFirst;
        let activeEndDate = endFilter ? new Date(endFilter) : new Date();
        
        // Ensure denominator is at least 1 month to avoid division by zero
        let monthDiff = (activeEndDate.getFullYear() - activeStartDate.getFullYear()) * 12 + (activeEndDate.getMonth() - activeStartDate.getMonth()) + 1;
        if (monthDiff < 1) monthDiff = 1;

        const avgMonthlyIncome = totalInc / monthDiff;
        const avgMonthlyExpense = totalExp / monthDiff;
        const avgMonthlyNet = netProfitLoss / monthDiff;

        setReportSummary({
            totalIncome: totalInc,
            totalExpenses: totalExp,
            profitLoss: netProfitLoss,
            incomeCount: processedAndFilteredIncome.length,
            expenseCount: filteredExp.length,
            filteredInc: processedAndFilteredIncome,
            filteredExp,
            avgMonthlyIncome,
            avgMonthlyExpense,
            avgMonthlyNet,
            activeMonthSpan: monthDiff
        });
    }, [incomeData, expenseData, startDate, endDate]);

    useEffect(() => {
        if (!loading) calculateTotals();
    }, [loading, calculateTotals]);

    const handleDownloadPdf = () => {
        if (!reportSummary) return;
        const doc = new jsPDF();
        const primaryBlue = [37, 99, 235];

        doc.setFillColor(...primaryBlue);
        doc.rect(0, 0, 210, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.text("GKM INVEST", 14, 25);

        doc.setFontSize(10);
        doc.text("CONSOLIDATED PROFIT & LOSS STATEMENT (WITH MONTHLY AVERAGES)", 14, 33);

        autoTable(doc, {
            startY: 50,
            head: [['Financial Metric', 'Total Aggregate', 'Monthly Average (Since Jan)']],
            body: [
                ['Total Revenue', formatRWF(reportSummary.totalIncome), formatRWF(reportSummary.avgMonthlyIncome)],
                ['Total Expenses', `(${formatRWF(reportSummary.totalExpenses)})`, `(${formatRWF(reportSummary.avgMonthlyExpense)})`],
                ['Net Profit / Loss', formatRWF(reportSummary.profitLoss), formatRWF(reportSummary.avgMonthlyNet)]
            ],
            theme: 'grid'
        });

        doc.save(`GKM_Report_${Date.now()}.pdf`);
    };

    const handleDownloadExcel = async () => {
        if (!reportSummary) return;

        const workbook = new ExcelJS.Workbook();
        
        // ==========================================
        // TAB 1: SUMMARY STATEMENT OVERVIEW
        // ==========================================
        const summarySheet = workbook.addWorksheet("P&L Summary");
        summarySheet.columns = [
            { id: 'A', width: 30 },
            { id: 'B', width: 24 },
            { id: 'C', width: 26 }
        ];

        summarySheet.mergeCells('A1:C1');
        const titleCell = summarySheet.getCell('A1');
        titleCell.value = 'GKM INVEST COMPANY LTD';
        titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E40AF' } };
        summarySheet.getRow(1).height = 36;

        summarySheet.mergeCells('A2:C2');
        const subtitleCell = summarySheet.getCell('A2');
        subtitleCell.value = 'CONSOLIDATED PROFIT & LOSS STATEMENT & MONTHLY AVERAGES';
        subtitleCell.font = { size: 12, bold: true, color: { argb: '1F2937' } };
        subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        summarySheet.getRow(2).height = 24;

        summarySheet.mergeCells('A3:C3');
        const dateCell = summarySheet.getCell('A3');
        dateCell.value = `Reporting Period Span: ${reportSummary.activeMonthSpan} Months | Run Date: ${new Date().toLocaleDateString()}`;
        dateCell.font = { size: 9, italic: true, color: { argb: '4B5563' } };
        dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
        summarySheet.getRow(3).height = 20;

        const summaryHeader = summarySheet.getRow(5);
        summaryHeader.values = ['Financial Category', 'Total Aggregate', 'Monthly Average'];
        summaryHeader.height = 24;
        summaryHeader.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2563EB' } };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        });

        const incRow = summarySheet.addRow(['Total Revenue (Inflow)', reportSummary.totalIncome, reportSummary.avgMonthlyIncome]);
        incRow.getCell(2).numFmt = '"RWF" #,##0.00';
        incRow.getCell(3).numFmt = '"RWF" #,##0.00';
        incRow.getCell(2).font = { color: { argb: '15803D' }, bold: true };
        incRow.getCell(3).font = { color: { argb: '15803D' }, bold: true };

        const expRow = summarySheet.addRow(['Total Expenses (Outflow)', reportSummary.totalExpenses, reportSummary.avgMonthlyExpense]);
        expRow.getCell(2).numFmt = '"RWF" #,##0.00';
        expRow.getCell(3).numFmt = '"RWF" #,##0.00';
        expRow.getCell(2).font = { color: { argb: 'B91C1C' }, bold: true };
        expRow.getCell(3).font = { color: { argb: 'B91C1C' }, bold: true };

        const netRow = summarySheet.addRow(['NET OPERATING VALUE', reportSummary.profitLoss, reportSummary.avgMonthlyNet]);
        netRow.height = 26;
        netRow.font = { bold: true, size: 11 };
        netRow.eachCell((cell, colIndex) => {
            cell.fill = { 
                type: 'pattern', 
                pattern: 'solid', 
                fgColor: { argb: reportSummary.profitLoss >= 0 ? 'DCFCE7' : 'FEE2E2' } 
            };
            if (colIndex > 1) {
                cell.numFmt = '"RWF" #,##0.00';
                cell.font = { bold: true, color: { argb: reportSummary.profitLoss >= 0 ? '166534' : '991B1B' } };
            }
        });

        summarySheet.eachRow((row, rowNum) => {
            if (rowNum < 5) return;
            row.eachCell(c => {
                c.border = {
                    top: { style: 'thin', color: { argb: 'E2E8F0' } },
                    bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
                    left: { style: 'thin', color: { argb: 'E2E8F0' } },
                    right: { style: 'thin', color: { argb: 'E2E8F0' } }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `GKM_ProfitLoss_${Date.now()}.xlsx`);
    };

    return (
        <div className="pl-report-wrapper">
            <ManagerSidebar />
            <main className="pl-main-content">

                <header className="pl-header">
                    <div className="header-left">
                        <h1>Financial Performance</h1>
                        <p>Consolidated profit, loss, and monthly average metrics tracking from January.</p>
                    </div>

                    <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                        <button className="pl-export-btn" onClick={handleDownloadPdf}>
                            <FaFilePdf /> Export PDF
                        </button>
                        
                        <button className="pl-export-btn ExcelJS-btn" onClick={handleDownloadExcel} style={{ backgroundColor: '#16a34a' }}>
                            <FaDownload /> Export Excel
                        </button>
                    </div>
                </header>

                {reportSummary && (
                    <div className="pl-stats-grid">
                        <div className="pl-card income-card">
                            <div className="pl-icon-box"><FaArrowUp /></div>
                            <div className="pl-info">
                                <label>Total Revenue</label>
                                <h3>{formatRWF(reportSummary.totalIncome)}</h3>
                                <span className="monthly-avg-tag">Avg: {formatRWF(reportSummary.avgMonthlyIncome)} / mo</span>
                            </div>
                        </div>

                        <div className="pl-card expense-card">
                            <div className="pl-icon-box"><FaArrowDown /></div>
                            <div className="pl-info">
                                <label>Total Expenses</label>
                                <h3>{formatRWF(reportSummary.totalExpenses)}</h3>
                                <span className="monthly-avg-tag">Avg: {formatRWF(reportSummary.avgMonthlyExpense)} / mo</span>
                            </div>
                        </div>

                        <div className={`pl-card net-card ${reportSummary.profitLoss >= 0 ? 'is-profit' : 'is-loss'}`}>
                            <div className="pl-icon-box"><FaBalanceScale /></div>
                            <div className="pl-info">
                                <label>Net Profit / Loss</label>
                                <h3>{formatRWF(reportSummary.profitLoss)}</h3>
                                <span className="monthly-avg-tag">Avg: {formatRWF(reportSummary.avgMonthlyNet)} / mo</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="pl-filter-bar">
                    <div className="pl-input-group">
                        <label><FaCalendarAlt /> Reporting Period</label>
                        <div className="inputs">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            <span>to</span>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                    </div>

                    <button className="pl-refresh-btn" onClick={calculateTotals}>
                        Apply Filters
                    </button>
                </div>

                <div className="pl-table-grid">
                    {/* INCOME STREAMS */}
                    <section className="pl-table-section">
                        <div className="section-title">
                            <FaMoneyBillWave className="txt-green" />
                            <h4>Income Streams (Monthly Distributed)</h4>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th>Guest Source</th>
                                    <th>Month Context</th>
                                    <th className="right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportSummary?.filteredInc.slice(0, 10).map((t, i) => (
                                    <tr key={i}>
                                        <td>{t.guest_name || 'Tenant'}</td>
                                        <td>{new Date(t.arrival_date).toLocaleString('default', {month:'long', year:'numeric'})}</td>
                                        <td className="right txt-green">
                                            +{formatRWF(t.rent_rwf)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    {/* EXPENDITURES */}
                    <section className="pl-table-section">
                        <div className="section-title">
                            <FaChartLine className="txt-red" />
                            <h4>Expenditures</h4>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Date</th>
                                    <th className="right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportSummary?.filteredExp.slice(0, 10).map((e, i) => (
                                    <tr key={i}>
                                        <td>{e.category || 'Expense'}</td>
                                        <td>{e.Date || e.date ? new Date(e.Date || e.date).toLocaleDateString() : 'N/A'}</td>
                                        <td className="right txt-red">
                                            -{formatRWF(e.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default ProfitLossReport;